import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/hooks/use-auth";
import { SITE_OWNER_EMAIL } from "@shared/constants";
import { LayoutTemplate, Shield, ExternalLink } from "lucide-react";
import type { LandingPageDefinition } from "@shared/landing-pages";

type LandingPageStatsRow = LandingPageDefinition & {
  visitorCount: number;
  subscriberCount: number;
};

export default function AdminLandingPages() {
  const { user } = useAuth();
  const isSiteOwner = (user?.email || "").toLowerCase() === SITE_OWNER_EMAIL;

  const { data: pages = [], isLoading } = useQuery<LandingPageStatsRow[]>({
    queryKey: ["/api/admin/landing-pages"],
    queryFn: async () => {
      const res = await fetch("/api/admin/landing-pages", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load landing page stats");
      return res.json();
    },
    enabled: isSiteOwner,
  });

  if (!isSiteOwner) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Shield size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-display font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground">This page is only accessible to the site owner.</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div
            className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"
            aria-hidden="true"
          />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Admin</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <LayoutTemplate size={28} className="text-primary shrink-0" aria-hidden="true" />
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold" data-testid="text-landing-pages-title">
                  Landing Pages
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Visitor and newsletter signup totals per marketing landing page (all time).
              </p>
            </div>

            {isLoading ? (
              <div className="text-center py-20 text-muted-foreground">Loading landing pages...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-landing-pages">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      <th className="pb-3 pr-4 font-semibold">Page name</th>
                      <th className="pb-3 pr-4 font-semibold min-w-[220px]">ICP profile</th>
                      <th className="pb-3 pr-4 font-semibold">URL</th>
                      <th className="pb-3 pr-4 font-semibold text-right">Visitors</th>
                      <th className="pb-3 font-semibold text-right">Newsletter subs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((page) => (
                      <tr
                        key={page.id}
                        className="border-b border-white/5 hover:bg-white/[0.02]"
                        data-testid={`row-landing-page-${page.id}`}
                      >
                        <td className="py-4 pr-4 font-medium" data-testid={`text-page-name-${page.id}`}>
                          {page.name}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground max-w-md" data-testid={`text-icp-${page.id}`}>
                          {page.icpProfile}
                        </td>
                        <td className="py-4 pr-4">
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            data-testid={`link-url-${page.id}`}
                          >
                            {page.url.replace("https://www.mattrebeiro.com", "")}
                            <ExternalLink size={12} aria-hidden="true" />
                          </a>
                        </td>
                        <td className="py-4 pr-4 text-right tabular-nums" data-testid={`text-visitors-${page.id}`}>
                          {page.visitorCount.toLocaleString()}
                        </td>
                        <td className="py-4 text-right tabular-nums" data-testid={`text-subs-${page.id}`}>
                          {page.subscriberCount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pages.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No landing pages configured. Add entries in <code className="text-xs">shared/landing-pages.ts</code>.
                  </div>
                )}
              </div>
            )}

            <p className="mt-8 text-sm text-muted-foreground">
              <Link href="/admin/users" className="text-primary hover:underline">
                User Management
              </Link>
              {" · "}
              <Link href="/admin/users#newsletter-subs" className="text-primary hover:underline">
                Newsletter Subs
              </Link>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
